import { ParsedUrlQuery } from 'querystring';
import * as url from 'url';
import { captureContentUpdate } from './oneDsUtil';

// Convert search parameter from those in `new URL()` to an old style (`url.parse()`).
// Used in `urlParse` below.
const newUrlApiSearchParamsToOld = (params: url.URLSearchParams): ParsedUrlQuery => {
  const query: ParsedUrlQuery = {};
  const entries = params.entries();
  let { done, value } = entries.next();
  while (!done) {
    const [varName, varValue]: [string, string?] = value;
    if (Object.prototype.hasOwnProperty.call(query, varName)) {
      if (typeof query[varName] === 'string') {
        query[varName] = [query[varName] as string, varValue || ''];
      } else {
        (query[varName] as string[]).push(varValue || '');
      }
    } else {
      query[varName] = varValue || '';
    }
    ({ done, value } = entries.next());
  }
  return query;
};

/**
 * URL parsing following the conventions of `url.parse()` using non-deprecated APIs.
 * **Note**: this implementation uses the newer `URL()` API, which is a lot less lenient than the original.
 * It will also set the resulting `port` to null if an explicit port matches the default for the protocol.
 * @see {@link https://nodejs.org/dist/latest-v18.x/docs/api/url.html#urlparseurlstring-parsequerystring-slashesdenotehost}
 * @param {string} urlStr
 * @param {boolean} parseQueryString
 * @param {boolean?} _slashesDenoteHost - dummy argument used just for backwards compatibility,
 *   as URLs without protocol will not parse.
 * @returns {url.UrlWithStringQuery|url.UrlWithParsedQuery} an object containing the components of the URL
 * @throws {TypeError} much like `url.parse()`, it will throw if it cannot parse `urlStr`.
 */
export const urlParse = (
  urlStr: string,
  parseQueryString: boolean = false,
  _slashesDenoteHost?: boolean
): url.UrlWithStringQuery | url.UrlWithParsedQuery => {
  try {
    const newApiUrl = new URL(urlStr);
    const protocol: string | null = newApiUrl.protocol;
    const slashes: boolean | null = /^[a-zA-Z][a-zA-Z0-9+.-]+:\/\//.test(urlStr) || null;
    const auth: string | null =
      newApiUrl.username + (newApiUrl.password === '' ? '' : ':' + newApiUrl.password) || null;
    const host: string | null = newApiUrl.host;
    const port: string | null = newApiUrl.port || null;
    const hostname: string | null = newApiUrl.hostname.startsWith('[')
      ? newApiUrl.hostname.slice(1, -1)
      : newApiUrl.hostname;
    const hash: string | null = newApiUrl.hash || null;
    const search: string | null = newApiUrl.search || null;
    const query: string | ParsedUrlQuery | null = parseQueryString
      ? newUrlApiSearchParamsToOld(newApiUrl.searchParams)
      : search === null
      ? null
      : search.substring(1);
    const pathname: string | null = newApiUrl.pathname || null;
    const path: string | null = newApiUrl.pathname + newApiUrl.search || null;
    const href: string = newApiUrl.href;
    return {
      protocol,
      slashes,
      auth,
      host,
      port,
      hostname,
      hash,
      search,
      query,
      pathname,
      path,
      href,
    } as url.UrlWithParsedQuery | url.UrlWithStringQuery;
  } catch (err) {
    const [protoAuthorityAndPath, searchOnly] = urlStr.split('#', 2)[0].split('?', 2);
    const sourceApp = searchOnly
      .split('&')
      .filter(keyAndValueStr => keyAndValueStr.toLowerCase().startsWith('sourceapp='))
      .map(keyAndValueStr => keyAndValueStr.split('=', 2)[1])[0];
    // Logging corner cases without exposing PI/PII.
    const errorMessage = String(err);
    const logHandlerTags = {
      content: {
        contentId: 'urlParse.Exception',
        errorMessage,
        caller: urlParse.caller.name,
        url: protoAuthorityAndPath,
        sourceApp,
      },
      behavior: 'CONTACT',
    };
    captureContentUpdate(logHandlerTags);
    throw err;
  }
};
